import { useTranslation } from "react-i18next";
import LeafSearchInput from "../../../../../../common/leaf/LeafSearchInput/LeafSearchInput";
import { useAppSelector, useAppDispatch } from "../../../../../../store";
import defaultContent from "../../../../../../content/profile";
import { getLoginAffAbo } from "../../../../../../reducers/model/boot";
import useGetProfileOrders from "./hooks/useGetProfileOrders";
import { updateSearchKeyword } from "../../../../../../reducers/order/profileOrders";
import "./OrderHistoryTab.scss";
import ProfileLoader from "../../../../../../components/ABOProfile/ProfileLoader/ProfileLoader";
import {
  searchOrderClickTrack,
  profileOrderHistoryTabErrorTrack,
} from "../../../../../../common/tealium/CustomerProfile/Profile/ProfileTabViewsTrack/OrderHistoryTabTrack";
import { useGetProfileQuery } from "../../../../../../services/Profile/profileAPI";
import { getBonusPeriod } from "../../../../../../common/util/common";
import OrdersByPeriod from "../../../../../Customers/OrderHistory/OrderHistoryTable/OrdersByPeriod/OrdersByPeriod";
import { includeGuestCustomerBN } from "../../../../../../reducers/model/order";
import { ErrorTile } from "../../../../../../common/components/ErrorTile/ErrorTile";
import { useLazyGetProfileOrdersQuery } from "../../../../../../services/Customer/CustomerOrderList/customerOrderListAPI";
import { useEffect, useRef, useState } from "react";
import { storedLocale } from "../../../../../Authentication/util";

const OrderHistoryTab = () => {
  const { t } = useTranslation(["profile"]);
  const locale = storedLocale();

  const dispatch = useAppDispatch();

  const { selectedPeriod } = useAppSelector((state) => state.period);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState<string | null>(null);

  const profileABO = useAppSelector((state) => state.profile.affAbo);
  const customerIds = Number(profileABO.split("-")[1]); // extract customer number only

  // Confidential customers (e.g. Who's New "New Customer") query orders under the sponsor ABO.
  const confidentialView = useAppSelector((state) => state.profile.confidentialView);
  const requestWithSponsor = useAppSelector((state) => state.profile.requestWithSponsor);

  const boot = useAppSelector((state) => state.boot);
  const affAbo = getLoginAffAbo({ boot });

  const { isFetching: isFetchingProfile, data: profile } = useGetProfileQuery({
    period: selectedPeriod,
    affAbo: profileABO,
  });

  const {
    isFetching: isFetchingOrderList = false,
    NextPageNum,
    NextPeriod,
    PageSize,
    orderGroups,
    allOrderCount,
    orderSearchKeyword = "",
    isError = false,
  } = useAppSelector((state) => state.profileOrders);

  const enableGuestCustomer = boot?.configuration?.globalCML?.enableGuestCustomer;
  const minDigit = boot?.configuration?.globalCML?.orderList?.search?.minDigit;

  const _nextPeriod = `${NextPeriod !== 0 ? NextPeriod : selectedPeriod}`;

  const profileOrdersQueryParams = {
    affAbo,
    pageSize: PageSize,
    pageNum: NextPageNum,
    period: _nextPeriod,
    sortValue: "ORDER_DATE",
    sortOrder: "DESC",
    customerIds,
    searchBy: orderSearchKeyword,
    locale: locale,
    requestWithSponsor: confidentialView || requestWithSponsor,
    ...includeGuestCustomerBN(enableGuestCustomer),
  };

  useEffect(() => {
    if (orderSearchKeyword || searchInput === "") {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [orderSearchKeyword, searchInput]);

  const entryDate = profile?.aboInfo?.entryDate;

  const customerEntryDatePeriod = entryDate && entryDate !== "--" ? getBonusPeriod(new Date(entryDate)) : 0;

  // auto pre-load the data for first time
  const haveNextPage =
    NextPageNum !== null && customerEntryDatePeriod !== null && _nextPeriod >= customerEntryDatePeriod;
  const isFetching = isFetchingOrderList === false && isFetchingProfile === false;
  const autoFetch = haveNextPage && isFetching === true && isError === false;

  const [triggerOrders] = useLazyGetProfileOrdersQuery();

  useGetProfileOrders({ ...profileOrdersQueryParams, autoFetch });

  const handleRefetch = () => {
    triggerOrders(profileOrdersQueryParams);
  };

  // when component mounted, load the auto load page data
  useGetProfileOrders({ ...profileOrdersQueryParams, autoFetch });

  const updateSearch = (input: string) => {
    setSearchInput(input);
    dispatch(updateSearchKeyword(input));
    searchOrderClickTrack();
  };

  const resetSearch = () => {
    setSearchInput("");
    dispatch(updateSearchKeyword(""));
  };

  if (isError === true) {
    profileOrderHistoryTabErrorTrack();
  }

  const LoaderView = () => {
    return <ProfileLoader row={PageSize || 10} card={true} isFetching={isFetchingOrderList} />;
  };

  const ErrorView = () => {
    return (
      <ErrorTile
        clickFunction={handleRefetch}
        errorStatus={isError}
        isLoading={!isError}
        tealiumTrackId="order-history-tab"
      />
    );
  };

  return (
    <div className="order-history-tab">
      <div className="order-history-tab__search-container">
        <div className="order-history-tab__search">
          <LeafSearchInput
            minDigit={minDigit}
            placeholder={`${t(
              "customerProfile.orderHistoryTab.search",
              defaultContent["customerProfile"]["orderHistoryTab"]["search"],
            )}`}
            callback={updateSearch}
            clearOnClick={resetSearch}
            debounceMS={500}
            searchKeyword={orderSearchKeyword}
            key={orderSearchKeyword}
            inputRef={inputRef}
            hideInputArrows={true}
          />
        </div>
      </div>
      <div>
        <OrdersByPeriod
          orderGroups={orderGroups}
          filterApplied={false}
          searchApplied={orderSearchKeyword !== ""}
          isCompact={true}
          LoaderView={LoaderView}
          ErrorView={ErrorView}
        ></OrdersByPeriod>
      </div>
    </div>
  );
};

export default OrderHistoryTab;
